import React, { useState, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { CANVAS_W, CANVAS_H } from '../../hooks/useGameEngine';
import { getColorHex } from './ColorPicker';

const HALF = 20;

// Convert line points to SVG path
function pointsToPath(points) {
  if (!points || points.length < 2) return '';
  // Round coordinates to avoid floating point issues in SVG
  let d = `M ${Math.round(points[0].x)} ${Math.round(points[0].y)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${Math.round(points[i].x)} ${Math.round(points[i].y)}`;
  }
  return d;
}

// Base colors - used as fallback when no color specified
const SHAPE_COLORS = {
  cube: '#18181B', sphere: '#2563EB', pyramid: '#10B981',
  hexagon: '#7C3AED', diamond: '#F59E0B', star: '#EF4444',
  void: '#71717A', chaos: '#EA580C',
  // Special shapes
  cross: '#DC2626', arrow: '#0891B2', crescent: '#7C3AED',
  bolt: '#FBBF24', wave: '#06B6D4', heart: '#EC4899',
  cloud: '#94A3B8', spiral: '#8B5CF6', fragment: '#64748B', eye: '#14B8A6',
};

// Vibrant colors for porous/equilibrium state - more saturated, alive
const SHAPE_COLORS_VIBRANT = {
  cube: '#000000', sphere: '#1D4ED8', pyramid: '#059669',
  hexagon: '#6D28D9', diamond: '#D97706', star: '#DC2626',
  void: '#52525B', chaos: '#C2410C',
  // Special shapes - vibrant versions
  cross: '#B91C1C', arrow: '#0E7490', crescent: '#6D28D9',
  bolt: '#F59E0B', wave: '#0891B2', heart: '#DB2777',
  cloud: '#64748B', spiral: '#7C3AED', fragment: '#475569', eye: '#0D9488',
};

// Desaturated colors for destruction - life draining
const SHAPE_COLORS_FADED = {
  cube: '#52525B', sphere: '#6B7280', pyramid: '#6B7280',
  hexagon: '#9CA3AF', diamond: '#9CA3AF', star: '#9CA3AF',
  void: '#A1A1AA', chaos: '#9CA3AF',
  // Special shapes - faded versions
  cross: '#9CA3AF', arrow: '#9CA3AF', crescent: '#9CA3AF',
  bolt: '#9CA3AF', wave: '#9CA3AF', heart: '#9CA3AF',
  cloud: '#9CA3AF', spiral: '#9CA3AF', fragment: '#9CA3AF', eye: '#9CA3AF',
};

// Get piece color - uses player's selected color if available, otherwise falls back to type color
const getPieceColor = (piece, atmosphericZone, phase) => {
  // If piece has a custom color, use it (except in destruction phase)
  if (piece.color && phase !== 'destruction') {
    return getColorHex(piece.color);
  }
  
  // Fall back to type-based colors
  if (phase === 'destruction') {
    return SHAPE_COLORS_FADED[piece.type] || '#9CA3AF';
  }
  if (atmosphericZone === 'porous') {
    return SHAPE_COLORS_VIBRANT[piece.type] || SHAPE_COLORS[piece.type];
  }
  return SHAPE_COLORS[piece.type] || '#18181B';
};

// Poetic destruction messages - the form closed, not "destroyed"
const DESTRUCTION_MESSAGES = [
  "Form closed upon itself.",
  "Certainty became structure. Structure could not breathe.",
  "The form lost its permeability.",
  "What was open has hardened.",
  "The structure forgot the unknown.",
  "Rigidity invited its own ending.",
  "Nothing entered anymore.",
  "Completion left no room for becoming.",
  "When nothing can enter, nothing can continue.",
  "The form became answer instead of question.",
  "Too certain.",
  "Closed.",
  "No passage remained.",
  "Sealed.",
  "Impenetrable.",
];

function getRandomDestructionMessage() {
  return DESTRUCTION_MESSAGES[Math.floor(Math.random() * DESTRUCTION_MESSAGES.length)];
}

function shapeSvg(type, cx, cy, colorOverride, rotation = 0) {
  const color = colorOverride || SHAPE_COLORS[type] || '#18181B';
  const h = HALF;
  
  // Generate the shape SVG
  let shapeContent = '';
  switch (type) {
    case 'cube': shapeContent = `<rect x="${cx - h}" y="${cy - h}" width="${h * 2}" height="${h * 2}" fill="${color}" rx="3"/>`; break;
    case 'sphere': shapeContent = `<circle cx="${cx}" cy="${cy}" r="${h}" fill="${color}"/>`; break;
    case 'pyramid': shapeContent = `<polygon points="${cx},${cy - h} ${cx - h},${cy + h} ${cx + h},${cy + h}" fill="${color}"/>`; break;
    case 'hexagon': {
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        return `${cx + h * Math.cos(a)},${cy + h * Math.sin(a)}`;
      }).join(' ');
      shapeContent = `<polygon points="${pts}" fill="${color}"/>`;
      break;
    }
    case 'diamond': shapeContent = `<polygon points="${cx},${cy - h} ${cx + h * 0.7},${cy} ${cx},${cy + h} ${cx - h * 0.7},${cy}" fill="${color}"/>`; break;
    case 'star': {
      const outer = h, inner = h * 0.4;
      const pts = Array.from({ length: 10 }, (_, i) => {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const r = i % 2 === 0 ? outer : inner;
        return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
      }).join(' ');
      shapeContent = `<polygon points="${pts}" fill="${color}"/>`;
      break;
    }
    case 'void': shapeContent = `<rect x="${cx - h}" y="${cy - h}" width="${h * 2}" height="${h * 2}" fill="none" stroke="#71717A" stroke-width="2" stroke-dasharray="4 4" rx="3"/>`; break;
    case 'chaos': shapeContent = `<rect x="${cx - h}" y="${cy - h}" width="${h * 2}" height="${h * 2}" fill="#EA580C" rx="3" opacity="0.8"/><line x1="${cx - h / 2}" y1="${cy - h / 2}" x2="${cx + h / 2}" y2="${cy + h / 2}" stroke="white" stroke-width="2.5"/><line x1="${cx + h / 2}" y1="${cy - h / 2}" x2="${cx - h / 2}" y2="${cy + h / 2}" stroke="white" stroke-width="2.5"/>`; break;
    // Special shapes - won from equilibrium lottery
    case 'cross': shapeContent = `<path d="M${cx-h/3},${cy-h} h${h*2/3} v${h*2/3} h${h*2/3} v${h*2/3} h-${h*2/3} v${h*2/3} h-${h*2/3} v-${h*2/3} h-${h*2/3} v-${h*2/3} h${h*2/3}z" fill="${color}"/>`; break;
    case 'arrow': shapeContent = `<polygon points="${cx+h},${cy} ${cx-h/2},${cy-h} ${cx-h/2},${cy-h/3} ${cx-h},${cy-h/3} ${cx-h},${cy+h/3} ${cx-h/2},${cy+h/3} ${cx-h/2},${cy+h}" fill="${color}"/>`; break;
    case 'crescent': shapeContent = `<path d="M${cx+h/2},${cy-h} A${h},${h} 0 1,0 ${cx+h/2},${cy+h} A${h*0.6},${h*0.6} 0 1,1 ${cx+h/2},${cy-h}" fill="${color}"/>`; break;
    case 'bolt': shapeContent = `<polygon points="${cx+h/2},${cy-h} ${cx-h/3},${cy-h/4} ${cx+h/4},${cy-h/4} ${cx-h/2},${cy+h} ${cx+h/3},${cy+h/4} ${cx-h/4},${cy+h/4}" fill="${color}"/>`; break;
    case 'wave': shapeContent = `<path d="M${cx-h},${cy} Q${cx-h/2},${cy-h} ${cx},${cy} Q${cx+h/2},${cy+h} ${cx+h},${cy}" stroke="${color}" stroke-width="4" fill="none"/>`; break;
    case 'heart': shapeContent = `<path d="M${cx},${cy+h/2} C${cx-h*1.2},${cy-h/2} ${cx-h},${cy-h} ${cx},${cy-h/3} C${cx+h},${cy-h} ${cx+h*1.2},${cy-h/2} ${cx},${cy+h/2}z" fill="${color}"/>`; break;
    case 'cloud': shapeContent = `<ellipse cx="${cx}" cy="${cy}" rx="${h}" ry="${h*0.6}" fill="${color}"/><circle cx="${cx-h/2}" cy="${cy-h/3}" r="${h/2}" fill="${color}"/><circle cx="${cx+h/2}" cy="${cy-h/3}" r="${h/2}" fill="${color}"/>`; break;
    case 'spiral': shapeContent = `<circle cx="${cx}" cy="${cy}" r="${h}" fill="none" stroke="${color}" stroke-width="3"/><circle cx="${cx}" cy="${cy}" r="${h/2}" fill="${color}"/>`; break;
    case 'fragment': shapeContent = `<polygon points="${cx},${cy-h} ${cx+h},${cy+h/2} ${cx-h/2},${cy+h}" fill="${color}"/>`; break;
    case 'eye': shapeContent = `<ellipse cx="${cx}" cy="${cy}" rx="${h}" ry="${h*0.6}" fill="none" stroke="${color}" stroke-width="3"/><circle cx="${cx}" cy="${cy}" r="${h/3}" fill="${color}"/>`; break;
    default: return '';
  }
  
  // If rotation is specified, wrap in a group with transform
  if (rotation && rotation !== 0) {
    return `<g transform="rotate(${rotation} ${cx} ${cy})">${shapeContent}</g>`;
  }
  
  return shapeContent;
}

// Ghost shape for the HTML overlay — renders a mini SVG icon
function ghostMiniSvg(type, valid, selectedColor) {
  const s = 48;
  const h = 18;
  const cx = s / 2, cy = s / 2;
  // Use selected color if valid, red if invalid
  const fill = valid ? (selectedColor ? getColorHex(selectedColor) : (SHAPE_COLORS[type] || '#18181B')) : '#EF4444';
  const stroke = valid ? '#A1A1AA' : '#EF4444';
  let shape = '';
  switch (type) {
    case 'cube': shape = `<rect x="${cx-h}" y="${cy-h}" width="${h*2}" height="${h*2}" fill="${fill}" rx="3"/>`; break;
    case 'sphere': shape = `<circle cx="${cx}" cy="${cy}" r="${h}" fill="${fill}"/>`; break;
    case 'pyramid': shape = `<polygon points="${cx},${cy-h} ${cx-h},${cy+h} ${cx+h},${cy+h}" fill="${fill}"/>`; break;
    case 'hexagon': {
      const pts = Array.from({length:6},(_,i)=>{const a=(Math.PI/3)*i-Math.PI/6;return `${cx+h*Math.cos(a)},${cy+h*Math.sin(a)}`;}).join(' ');
      shape = `<polygon points="${pts}" fill="${fill}"/>`; break;
    }
    case 'diamond': shape = `<polygon points="${cx},${cy-h} ${cx+h*0.7},${cy} ${cx},${cy+h} ${cx-h*0.7},${cy}" fill="${fill}"/>`; break;
    case 'star': {
      const outer=h,inner=h*0.4;
      const pts = Array.from({length:10},(_,i)=>{const a=(Math.PI/5)*i-Math.PI/2;const r=i%2===0?outer:inner;return `${cx+r*Math.cos(a)},${cy+r*Math.sin(a)}`;}).join(' ');
      shape = `<polygon points="${pts}" fill="${fill}"/>`; break;
    }
    case 'void': shape = `<rect x="${cx-h}" y="${cy-h}" width="${h*2}" height="${h*2}" fill="none" stroke="${fill === '#EF4444' ? fill : '#71717A'}" stroke-width="2" stroke-dasharray="4 4" rx="3"/>`; break;
    // Special shapes
    case 'cross': shape = `<path d="M${cx-h/3},${cy-h} h${h*2/3} v${h*2/3} h${h*2/3} v${h*2/3} h-${h*2/3} v${h*2/3} h-${h*2/3} v-${h*2/3} h-${h*2/3} v-${h*2/3} h${h*2/3}z" fill="${fill}"/>`; break;
    case 'arrow': shape = `<polygon points="${cx+h},${cy} ${cx-h/2},${cy-h} ${cx-h/2},${cy-h/3} ${cx-h},${cy-h/3} ${cx-h},${cy+h/3} ${cx-h/2},${cy+h/3} ${cx-h/2},${cy+h}" fill="${fill}"/>`; break;
    case 'crescent': shape = `<path d="M${cx+h/2},${cy-h} A${h},${h} 0 1,0 ${cx+h/2},${cy+h} A${h*0.6},${h*0.6} 0 1,1 ${cx+h/2},${cy-h}" fill="${fill}"/>`; break;
    case 'bolt': shape = `<polygon points="${cx+h/2},${cy-h} ${cx-h/3},${cy-h/4} ${cx+h/4},${cy-h/4} ${cx-h/2},${cy+h} ${cx+h/3},${cy+h/4} ${cx-h/4},${cy+h/4}" fill="${fill}"/>`; break;
    case 'wave': shape = `<path d="M${cx-h},${cy} Q${cx-h/2},${cy-h} ${cx},${cy} Q${cx+h/2},${cy+h} ${cx+h},${cy}" stroke="${fill}" stroke-width="4" fill="none"/>`; break;
    case 'heart': shape = `<path d="M${cx},${cy+h/2} C${cx-h*1.2},${cy-h/2} ${cx-h},${cy-h} ${cx},${cy-h/3} C${cx+h},${cy-h} ${cx+h*1.2},${cy-h/2} ${cx},${cy+h/2}z" fill="${fill}"/>`; break;
    case 'cloud': shape = `<ellipse cx="${cx}" cy="${cy}" rx="${h}" ry="${h*0.6}" fill="${fill}"/><circle cx="${cx-h/2}" cy="${cy-h/3}" r="${h/2}" fill="${fill}"/><circle cx="${cx+h/2}" cy="${cy-h/3}" r="${h/2}" fill="${fill}"/>`; break;
    case 'spiral': shape = `<circle cx="${cx}" cy="${cy}" r="${h}" fill="none" stroke="${fill}" stroke-width="3"/><circle cx="${cx}" cy="${cy}" r="${h/2}" fill="${fill}"/>`; break;
    case 'fragment': shape = `<polygon points="${cx},${cy-h} ${cx+h},${cy+h/2} ${cx-h/2},${cy+h}" fill="${fill}"/>`; break;
    case 'eye': shape = `<ellipse cx="${cx}" cy="${cy}" rx="${h}" ry="${h*0.6}" fill="none" stroke="${fill}" stroke-width="3"/><circle cx="${cx}" cy="${cy}" r="${h/3}" fill="${fill}"/>`; break;
    default: break;
  }
  return `<circle cx="${cx}" cy="${cy}" r="${h+4}" fill="none" stroke="${stroke}" stroke-width="1.5" stroke-dasharray="5 3"/>` + shape;
}

function screenToSvg(e, svgRef) {
  const svg = svgRef.current;
  if (!svg) return null;
  try {
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const sp = pt.matrixTransform(ctm.inverse());
    if (sp.x >= 0 && sp.x <= CANVAS_W && sp.y >= 0 && sp.y <= CANVAS_H) {
      return { x: sp.x, y: sp.y };
    }
  } catch (err) { /* ignore */ }
  return null;
}

export const ConstructionCanvas = ({ 
  pieces, destroyingPieces, selectedPiece, selectedColor, phase, onPlace, atmosphericZone, equilibriumReached, equilibriumFading,
  // Drawing/Pen props
  drawnLines = [], currentDrawing = null, isDrawing = false, drawingMetrics = null,
  onStartDrawing, onContinueDrawing, onEndDrawing,
  // Rotate tool props
  onRotateTool,
  // Eraser props (void on lines)
  isErasing = false, isNearLineCheck, onStartErasing, onContinueErasing, onEndErasing,
}) => {
  // svgCoords: for game logic (placement, validity check)
  const [svgCoords, setSvgCoords] = useState(null);
  // screenPos: for visual ghost positioning (relative to container)
  const [screenPos, setScreenPos] = useState(null);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [destructionMessage, setDestructionMessage] = useState(() => getRandomDestructionMessage());
  const prevPhaseRef = useRef(phase);
  
  // Check if void is hovering near a line (for eraser mode display)
  const isNearLine = useMemo(() => {
    if (selectedPiece !== 'void' || !svgCoords) return false;
    // Use the function from game engine if available
    if (isNearLineCheck) {
      return isNearLineCheck(svgCoords.x, svgCoords.y);
    }
    return false;
  }, [selectedPiece, svgCoords, isNearLineCheck]);
  
  // Change destruction message each time destruction occurs
  React.useEffect(() => {
    if (phase === 'destruction' && prevPhaseRef.current !== 'destruction') {
      setDestructionMessage(getRandomDestructionMessage());
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  const isValid = useMemo(() => {
    if (!svgCoords || phase !== 'building') return false;
    // Shapes can now overlap - always valid placement
    // Only show invalid (red) for void block when hovering over a piece (eraser mode indicator)
    if (selectedPiece === 'void') {
      const overPiece = pieces.some(p => Math.sqrt((p.x - svgCoords.x) ** 2 + (p.y - svgCoords.y) ** 2) < 40);
      // In eraser mode (over piece), still show as valid since erasing is allowed
      return true;
    }
    return true; // All placements valid - overlapping allowed
  }, [svgCoords, pieces, phase, selectedPiece]);
  
  // Determine color palette based on atmospheric state
  const colorPalette = useMemo(() => {
    if (phase === 'destruction') return SHAPE_COLORS_FADED; // Losing vibrancy during disintegration
    if (equilibriumReached && !equilibriumFading) return SHAPE_COLORS_VIBRANT; // Sustained vibrancy
    if (atmosphericZone === 'porous') return SHAPE_COLORS_VIBRANT; // Breathing vibrancy
    if (equilibriumFading) return SHAPE_COLORS; // Returning to normal
    return SHAPE_COLORS; // Default
  }, [phase, equilibriumReached, equilibriumFading, atmosphericZone]);
  
  // Should colors breathe (pulsate) in porous zone?
  const shouldBreathe = atmosphericZone === 'porous' && !equilibriumReached;

  // Combined rendering of pieces and lines in placement order
  const combinedSvg = useMemo(() => {
    // Create array of all elements with their placement time
    const elements = [];
    
    // Add pieces (non-remnant, non-destroying)
    pieces.filter(p => !p.isRemnant).forEach(p => {
      const color = getPieceColor(p, atmosphericZone, phase);
      elements.push({
        placedAt: p.placedAt || 0,
        svg: shapeSvg(p.type, p.x, p.y, color, p.rotation || 0)
      });
    });
    
    // Add lines
    drawnLines.forEach(line => {
      const pathD = pointsToPath(line.points);
      if (pathD) {
        const strokeColor = line.color ? getColorHex(line.color) : '#18181B';
        // Use line.opacity for remnants, or destroying state
        const opacity = line.destroying ? 0.3 : (line.opacity || 1);
        // Remnant lines have dashed stroke
        const strokeDasharray = line.isRemnant ? '8 4' : 'none';
        elements.push({
          placedAt: line.placedAt || 0,
          svg: `<path d="${pathD}" stroke="${strokeColor}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity="${opacity}" stroke-dasharray="${strokeDasharray}" vector-effect="non-scaling-stroke"/>`
        });
      }
    });
    
    // Sort by placement time and join
    elements.sort((a, b) => a.placedAt - b.placedAt);
    return elements.map(e => e.svg).join('');
  }, [pieces, drawnLines, atmosphericZone, phase]);

  const destroyingSvg = useMemo(() => destroyingPieces.map(p => {
    // Use faded colors for destroying pieces - life draining away
    const color = SHAPE_COLORS_FADED[p.type] || SHAPE_COLORS_FADED.cube;
    return `<g class="piece-destroying" style="--exit-x:${p.exitX || 0}px;--exit-y:${p.exitY || 0}px">${shapeSvg(p.type, p.x, p.y, color, p.rotation || 0)}</g>`;
  }).join(''), [destroyingPieces]);
  
  // Generate SVG for current drawing (always on top while drawing)
  const currentLineSvg = useMemo(() => {
    if (!currentDrawing || !currentDrawing.points || currentDrawing.points.length < 2) return '';
    const pathD = pointsToPath(currentDrawing.points);
    if (!pathD) return '';
    const strokeColor = currentDrawing.color ? getColorHex(currentDrawing.color) : '#18181B';
    return `<path d="${pathD}" stroke="${strokeColor}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity="0.8" stroke-dasharray="8 4" vector-effect="non-scaling-stroke"/>`;
  }, [currentDrawing]);

  const handlePointerMove = useCallback((e) => {
    e.preventDefault();
    const pt = screenToSvg(e, svgRef);
    setSvgCoords(pt);
    // Screen position relative to container
    const container = containerRef.current;
    if (container && pt) {
      const rect = container.getBoundingClientRect();
      setScreenPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      
      const isPrimaryPressed = e.buttons === 1 || (e.pointerType === 'touch' && e.pressure > 0);
      
      // If pen mode and drawing, continue the line
      if (selectedPiece === 'pen' && isDrawing && onContinueDrawing && isPrimaryPressed) {
        onContinueDrawing(pt.x, pt.y);
      } else if (selectedPiece === 'pen' && isDrawing && !isPrimaryPressed) {
        if (onEndDrawing) onEndDrawing();
      }
      
      // If void mode and erasing, continue erasing
      if (selectedPiece === 'void' && isErasing && onContinueErasing && isPrimaryPressed) {
        onContinueErasing(pt.x, pt.y);
      } else if (selectedPiece === 'void' && isErasing && !isPrimaryPressed) {
        if (onEndErasing) onEndErasing();
      }
    } else {
      setScreenPos(null);
    }
  }, [selectedPiece, isDrawing, isErasing, onContinueDrawing, onEndDrawing, onContinueErasing, onEndErasing]);

  const handlePointerUp = useCallback((e) => {
    // Release pointer capture to stop tracking
    if (e.target && e.pointerId !== undefined) {
      try {
        e.target.releasePointerCapture(e.pointerId);
      } catch (err) { /* ignore if not captured */ }
    }
    
    const pt = screenToSvg(e, svgRef);
    
    // If pen mode, end drawing
    if (selectedPiece === 'pen') {
      if (onEndDrawing) onEndDrawing();
      return;
    }
    
    // If void mode and was erasing, end erasing
    if (selectedPiece === 'void' && isErasing) {
      if (onEndErasing) onEndErasing();
      return;
    }
    
    // Normal shape placement
    if (pt) onPlace(pt.x, pt.y);
  }, [onPlace, selectedPiece, isErasing, onEndDrawing, onEndErasing]);

  const handlePointerLeave = useCallback(() => {
    setSvgCoords(null);
    setScreenPos(null);
    // End drawing if leaving canvas
    if (selectedPiece === 'pen' && isDrawing && onEndDrawing) {
      onEndDrawing();
    }
    // End erasing if leaving canvas
    if (selectedPiece === 'void' && isErasing && onEndErasing) {
      onEndErasing();
    }
  }, [selectedPiece, isDrawing, isErasing, onEndDrawing, onEndErasing]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    
    // Capture pointer to ensure we get all events until release
    if (e.target && e.pointerId !== undefined) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch (err) { /* ignore if already captured */ }
    }
    
    const pt = screenToSvg(e, svgRef);
    setSvgCoords(pt);
    const container = containerRef.current;
    if (container && pt) {
      const rect = container.getBoundingClientRect();
      setScreenPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      
      // If pen mode, start drawing
      if (selectedPiece === 'pen' && onStartDrawing) {
        onStartDrawing(pt.x, pt.y);
      }
      
      // If rotate mode, try to rotate a piece
      if (selectedPiece === 'rotate' && onRotateTool) {
        onRotateTool(pt.x, pt.y);
        return; // Don't proceed to normal placement
      }
      
      // If void mode and near a line, start erasing instead of placing
      if (selectedPiece === 'void' && onStartErasing) {
        const startedErasing = onStartErasing(pt.x, pt.y);
        // If we started erasing, don't proceed to normal placement
        if (startedErasing) return;
      }
    }
  }, [selectedPiece, onStartDrawing, onStartErasing, onRotateTool]);
  
  // Atmospheric visual adjustments
  const canvasClassName = useMemo(() => {
    let base = 'w-full h-auto border rounded-sm transition-all duration-700';
    switch (atmosphericZone) {
      case 'uncertain':
        return `${base} border-zinc-200 uncertain-canvas`;
      case 'porous':
        return `${base} border-stone-200 porous-breathing`;
      case 'nervous':
        return `${base} border-zinc-300 nervous-canvas`;
      case 'equilibrium':
        return `${base} border-zinc-100 equilibrium-canvas`;
      default:
        return `${base} border-zinc-200`;
    }
  }, [atmosphericZone]);
  
  // Visual style adjustments based on state
  const canvasStyle = useMemo(() => {
    const base = { maxHeight: '68vh', pointerEvents: 'none', overflow: 'visible' };
    
    if (phase === 'destruction') {
      // Desaturate during destruction - life draining
      return { ...base, filter: 'saturate(0.4) brightness(0.95)', transition: 'filter 0.5s ease-out' };
    }
    if (equilibriumReached && !equilibriumFading) {
      // Sustained vibrancy in equilibrium
      return { ...base, filter: 'saturate(1.15) brightness(1.02)', transition: 'filter 2s ease-out' };
    }
    if (atmosphericZone === 'porous' && shouldBreathe) {
      // Breathing vibrancy in porous zone
      return { ...base, filter: 'saturate(1.1)', transition: 'filter 1.5s ease-in-out' };
    }
    if (atmosphericZone === 'nervous') {
      return { ...base, filter: 'saturate(1.05) contrast(1.02)', transition: 'filter 0.3s ease' };
    }
    if (equilibriumFading) {
      // Returning to normal as entropy returns
      return { ...base, filter: 'saturate(1.0)', transition: 'filter 3s ease-out' };
    }
    return base;
  }, [phase, equilibriumReached, equilibriumFading, atmosphericZone, shouldBreathe]);

  const bgSvg = `
    <defs>
      <radialGradient id="field-bg" cx="50%" cy="50%" r="70%">
        <stop offset="0%" stop-color="#FDFDFD"/>
        <stop offset="100%" stop-color="#F4F4F5"/>
      </radialGradient>
    </defs>
    <rect width="${CANVAS_W}" height="${CANVAS_H}" fill="url(#field-bg)"/>
    <circle cx="${CANVAS_W / 2}" cy="${CANVAS_H / 2}" r="3" fill="#E4E4E7" opacity="0.5"/>
  `;

  // Pre-compute ghost mini SVG string
  // For pen tool: only show crosshair while actively drawing
  // For void near line: show void shape with subtle eraser indicator (no separate red icon)
  // For other tools: show shape shadow while hovering
  const ghostHtml = useMemo(() => {
    if (!screenPos || !svgCoords || phase !== 'building') return null;
    
    // For pen tool: only show crosshair while actively drawing
    if (selectedPiece === 'pen') {
      if (!isDrawing) return null; // No ghost when not drawing - show normal cursor
      const penColor = selectedColor ? getColorHex(selectedColor) : '#374151';
      return `<line x1="24" y1="12" x2="24" y2="36" stroke="${penColor}" stroke-width="2"/>
              <line x1="12" y1="24" x2="36" y2="24" stroke="${penColor}" stroke-width="2"/>
              <circle cx="24" cy="24" r="3" fill="${penColor}"/>`;
    }
    
    // For rotate tool: show rotate cursor
    if (selectedPiece === 'rotate') {
      return `<circle cx="24" cy="24" r="12" fill="none" stroke="#0EA5E9" stroke-width="2.5" stroke-dasharray="50 20"/>
              <polygon points="30,12 38,16 32,20" fill="#0EA5E9"/>`;
    }
    
    // For void tool near a line: show regular void ghost
    // Pass selected color to ghost for colored preview
    return ghostMiniSvg(selectedPiece, isValid, selectedColor);
  }, [screenPos, svgCoords, selectedPiece, phase, isValid, selectedColor, isDrawing]);

  return (
    <div className="relative w-full" data-testid="construction-canvas" ref={containerRef}>
      {/* SVG canvas — shapes only, no interaction */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        preserveAspectRatio="xMidYMid meet"
        className={canvasClassName}
        style={canvasStyle}
      >
        <g dangerouslySetInnerHTML={{ __html: bgSvg }} />
        
        {/* Combined pieces and lines in placement order */}
        <g dangerouslySetInnerHTML={{ __html: combinedSvg || '' }} />
        
        {/* Current drawing line (always on top while drawing) */}
        <g dangerouslySetInnerHTML={{ __html: currentLineSvg || '' }} />
        
        {/* Destroying pieces animation */}
        <g dangerouslySetInnerHTML={{ __html: destroyingSvg || '' }} />
        
        {/* Remnant markers */}
        <g dangerouslySetInnerHTML={{ __html: pieces.filter(p => p.isRemnant).map(p =>
          `<circle cx="${p.x}" cy="${p.y}" r="${HALF + 6}" fill="none" stroke="#10B981" stroke-width="1.5" opacity="0.35"/>`
        ).join('') }} />
      </svg>

      {/* Pointer interaction overlay */}
      <div
        className={`absolute inset-0 ${phase === 'building' ? (selectedPiece === 'pen' ? (isDrawing ? 'cursor-none' : 'cursor-default') : (svgCoords ? 'cursor-none' : 'cursor-default')) : 'cursor-default'}`}
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerLeave}
        data-testid="canvas-overlay"
      />

      {/* GHOST: HTML element that follows the actual mouse position */}
      {ghostHtml && screenPos && (
        <div
          className="pointer-events-none absolute z-20"
          style={{
            left: screenPos.x - 24,
            top: screenPos.y - 24,
            width: 48,
            height: 48,
            opacity: isValid ? 0.7 : 0.85,
            transition: 'opacity 0.15s',
          }}
          data-testid="ghost-preview"
        >
          <svg width="48" height="48" viewBox="0 0 48 48" dangerouslySetInnerHTML={{ __html: ghostHtml }} />
        </div>
      )}

      {/* "too close" label */}
      {svgCoords && phase === 'building' && !isValid && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none z-20">
          <span className="text-xs font-mono text-red-500 bg-white/95 px-3 py-1.5 rounded-sm border border-red-200 shadow-sm">
            too close
          </span>
        </div>
      )}

      {/* Destruction overlay - poetic message about form closing */}
      {phase === 'destruction' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="bg-zinc-50/95 border border-zinc-200 px-8 py-4 rounded-sm max-w-md text-center"
          >
            <span className="text-zinc-600 font-light text-base tracking-wide italic leading-relaxed" data-testid="destruction-label">
              {destructionMessage}
            </span>
          </motion.div>
        </div>
      )}
    </div>
  );
};
